import React from 'react';
import {
    XMarkIcon,
    ArrowDownTrayIcon,
    CheckCircleIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const DownloadProgress = ({
    progress = 0,
    fileName = '',
    fileSize = 0,
    downloadSpeed = 0,
    totalRecords = 0,
    processedRecords = 0,
    startTime = null,
    isVisible = false,
    error = null,
    onCancel = () => { }
}) => {
    if (!isVisible) return null;

    // Calculate time statistics
    const getTimeStats = () => {
        if (!startTime) return { elapsed: 0, remaining: 0 };

        const elapsed = (Date.now() - startTime) / 1000; // seconds
        const remaining = progress > 0 ? (elapsed * (100 - progress)) / progress : 0;

        return { elapsed, remaining };
    };

    const formatTime = (seconds) => {
        if (seconds < 60) return `${Math.round(seconds)}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.round(seconds % 60);
        return `${minutes}m ${remainingSeconds}s`;
    };

    const timeStats = getTimeStats();

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatSpeed = (bytesPerSecond) => {
        // Handle undefined, null, or invalid values
        if (bytesPerSecond === undefined || bytesPerSecond === null || bytesPerSecond === 0 || isNaN(bytesPerSecond)) {
            return '0 KB/s';
        }

        const k = 1024;
        const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
        const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
        return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getStatusInfo = () => {
        if (error) {
            return {
                title: 'Download Failed',
                subtitle: error,
                color: 'text-red-600',
                bgColor: 'bg-red-50',
                borderColor: 'border-red-200',
                icon: ExclamationCircleIcon
            };
        }

        if (progress >= 100) {
            return {
                title: 'Download Complete',
                subtitle: 'File has been downloaded successfully',
                color: 'text-green-600',
                bgColor: 'bg-green-50',
                borderColor: 'border-green-200',
                icon: CheckCircleIcon
            };
        }

        if (progress === 0 && processedRecords === 0) {
            return {
                title: 'Preparing Download',
                subtitle: 'Initializing file generation...',
                color: 'text-blue-600',
                bgColor: 'bg-blue-50',
                borderColor: 'border-blue-200',
                icon: ArrowDownTrayIcon
            };
        }

        return {
            title: 'Generating File',
            subtitle: 'Please wait while your file is being generated...',
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            icon: ArrowDownTrayIcon
        };
    };

    const statusInfo = getStatusInfo();
    const StatusIcon = statusInfo.icon;

    return (
        <div className="download-modal fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="modal-content bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
                {/* Header */}
                <div className={`${statusInfo.bgColor} ${statusInfo.borderColor} border-b px-6 py-4`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <StatusIcon className={`h-6 w-6 ${statusInfo.color}`} />
                            <div>
                                <h3 className={`font-semibold ${statusInfo.color}`}>
                                    {statusInfo.title}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {statusInfo.subtitle}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onCancel}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Progress Content */}
                <div className="p-6 space-y-6">
                    {/* Circular Progress */}
                    <div className="flex items-center justify-center">
                        <div className="relative w-24 h-24">
                            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                                {/* Background circle */}
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="40"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="none"
                                    className="text-gray-200"
                                />
                                {/* Progress circle */}
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="40"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="none"
                                    strokeDasharray={`${2 * Math.PI * 40}`}
                                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - progress / 100)}`}
                                    className={error ? 'text-red-500' : progress >= 100 ? 'text-green-500' : 'text-blue-500'}
                                    style={{
                                        transition: 'stroke-dashoffset 0.3s ease-in-out'
                                    }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className={`text-lg font-bold ${error ? 'text-red-600' : progress >= 100 ? 'text-green-600' : 'text-blue-600'}`}>
                                    {Math.round(progress)}%
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* File Information */}
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">File:</span>
                            <span className="text-sm text-gray-900 truncate max-w-48" title={fileName}>
                                {fileName}
                            </span>
                        </div>

                        {totalRecords > 0 && (
                            <>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Rows Written:</span>
                                    <span className="text-sm text-gray-900 font-mono">
                                        {processedRecords.toLocaleString()}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Remaining:</span>
                                    <span className="text-sm text-gray-900 font-mono">
                                        {(totalRecords - processedRecords).toLocaleString()}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Total Records:</span>
                                    <span className="text-sm text-gray-900 font-mono">
                                        {totalRecords.toLocaleString()}
                                    </span>
                                </div>
                            </>
                        )}

                        {fileSize > 0 && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">File Size:</span>
                                <span className="text-sm text-gray-900">
                                    {formatFileSize(fileSize)}
                                </span>
                            </div>
                        )}

                        {downloadSpeed !== undefined && downloadSpeed !== null && downloadSpeed > 0 && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Speed:</span>
                                <span className="text-sm text-gray-900 font-mono">
                                    {formatSpeed(downloadSpeed)}
                                </span>
                            </div>
                        )}

                        {startTime && timeStats.elapsed > 0 && (
                            <>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Elapsed:</span>
                                    <span className="text-sm text-gray-900 font-mono">
                                        {formatTime(timeStats.elapsed)}
                                    </span>
                                </div>

                                {progress > 0 && progress < 100 && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-700">Remaining:</span>
                                        <span className="text-sm text-gray-900 font-mono">
                                            {formatTime(timeStats.remaining)}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs text-gray-500">
                            <span>Progress</span>
                            <span>{Math.round(progress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className={`h-2 rounded-full transition-all duration-300 ${error ? 'bg-red-500' : progress >= 100 ? 'bg-green-500' : 'bg-blue-500'
                                    }`}
                                style={{ width: `${Math.min(progress, 100)}%` }}
                            />
                        </div>
                    </div>

                    {/* Action Button */}
                    {(error || progress >= 100) && (
                        <div className="flex justify-end pt-2">
                            <button
                                onClick={onCancel}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${error
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                    }`}
                            >
                                {error ? 'Close' : 'Done'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DownloadProgress; 